import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParallax, useMouseParallax } from "@/hooks/useParallax";
import { companyInfo } from "@/mocks/homeData";

interface AboutProps {
  onOpenModal: (type: string) => void;
}

function aboutStats(t: (key: string) => string) {
  return [
    { value: 5, suffix: "", label: t("about.statCountries"), icon: "ri-global-line", desc: t("about.statCountriesDesc") },
    { value: 200, suffix: "+", label: t("about.statSpecialists"), icon: "ri-team-line", desc: t("about.statSpecialistsDesc") },
    { value: 4.5, suffix: "/5", label: t("about.statRating"), icon: "ri-star-line", desc: t("about.statRatingDesc") },
    { value: 24, suffix: "/7", label: t("about.statMonitoring"), icon: "ri-time-line", desc: t("about.statMonitoringDesc") },
  ];
}

function aboutValues(t: (key: string) => string) {
  return [
    { icon: "ri-shield-check-line", title: t("about.valueTrust"), desc: t("about.valueTrustDesc") },
    { icon: "ri-lightbulb-flash-line", title: t("about.valueInnovation"), desc: t("about.valueInnovationDesc") },
    { icon: "ri-hand-heart-line", title: t("about.valueCommitment"), desc: t("about.valueCommitmentDesc") },
  ];
}

function useAnimatedCounter(target: number, decimals: number, isVisible: boolean, duration = 2000) {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isVisible || startedRef.current) return;
    startedRef.current = true;

    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * target);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isVisible, target, duration]);

  return decimals > 0 ? count.toFixed(decimals) : Math.floor(count).toString();
}

export default function About({ onOpenModal }: AboutProps) {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedValues, setExpandedValues] = useState<Set<number>>(new Set());
  const { ref: parallaxRef, offset } = useParallax(0.08);
  const { ref: mouseRef, pos: mousePos } = useMouseParallax(18);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative py-14 md:py-20 bg-[#FAFBFC] overflow-hidden"
    >
      {/* Humanized background image (kept light so readability stays intact) */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_818f54c56b82eae8993278d76fe31be7_787d378d.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#FAFBFC]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAFBFC]/92 via-[#FAFBFC]/60 to-[#FAFBFC]/92" />
      </div>

      {/* Transition gradients with neighbours (dark Certificaciones above, dark Services below) */}
      <div className="absolute top-0 left-0 right-0 h-20 md:h-28 bg-gradient-to-b from-secondary-950 to-transparent z-[6] pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-20 md:h-28 bg-gradient-to-t from-[#0A1220] to-transparent z-[6] pointer-events-none" />

      {/* Subtle background texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 40%, #4A7DBF 1px, transparent 1px), radial-gradient(circle at 80% 60%, #4A7DBF 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Parallax floating elements */}
      <div ref={parallaxRef} className="absolute inset-0 pointer-events-none overflow-hidden">
        {[
          { x: 4, y: 15, s: 120, d: 0.4 },
          { x: 94, y: 25, s: 80, d: 0.6 },
          { x: 8, y: 78, s: 90, d: 0.35 },
        ].map((n, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-[#4A7DBF]/[0.06]"
            style={{
              left: `${n.x}%`,
              top: `${n.y}%`,
              width: n.s,
              height: n.s,
              transform: `translate(-50%, -50%) translateY(${offset * n.d}px)`,
            }}
          />
        ))}
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* === TOP ROW: Image + Core Info === */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-10 md:mb-12">
            {/* LEFT: Large human editorial photo */}
            <div
              ref={mouseRef}
              className={`relative transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
            >
              {/* Mouse-following ambient glow */}
              <div
                className="absolute -inset-6 rounded-2xl opacity-40 blur-2xl"
                style={{
                  background: `radial-gradient(circle at ${50 + mousePos.x * 0.5}% ${50 + mousePos.y * 0.5}%, #4A7DBF 0%, transparent 70%)`,
                  transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
                }}
              />

              <div className="relative">
                {/* Clean frame */}
                <div className="relative rounded-2xl overflow-hidden aspect-[3/2]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_9397affe568fdb91708de1e8e87cde3e_07a13cd4.jpg"
                    alt="Equipo Openlab Secure"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />

                  {/* Subtle bottom fade with team caption */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1A2B4A]/90 via-[#1A2B4A]/40 to-transparent pt-20 pb-6 px-6">
                    <p className="text-white font-heading font-semibold text-base">
                      {t("about.teamTitle")}
                    </p>
                    <p className="text-white/70 text-sm mt-0.5">
                      {t("about.teamSubtitle")}
                    </p>
                  </div>
                </div>

                {/* ISO floating card - clean and minimal */}
                <div
                  className={`absolute -bottom-4 -right-4 md:-right-6 bg-white rounded-xl px-4 py-3 border border-[#E2E8F0] transition-all duration-700 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                  style={{ boxShadow: "0 4px 20px rgba(74,125,191,0.08)" }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#4A7DBF]/8">
                      <i className="ri-verified-badge-line text-[#4A7DBF] text-lg" />
                    </div>
                    <div>
                      <p className="text-[9px] font-label text-[#8899B0] uppercase tracking-wider">ISO 27001</p>
                      <p className="text-sm font-heading font-bold text-[#1A2B4A]">{t("about.isoCertified")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Story + Stats */}
            <div className={`transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <p className="text-[11px] font-label font-semibold text-[#4A7DBF] uppercase tracking-[0.2em] mb-3">
                {t("about.tag")}
              </p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-[#1A2B4A] mb-4 leading-tight">
                {t("about.title")}{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#4A7DBF] via-[#6B9BD4] to-[#5A8DC4] bg-clip-text text-transparent">
                    {t("about.highlight")}
                  </span>
                  <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-gradient-to-r from-[#4A7DBF]/30 via-[#6B9BD4]/60 to-transparent rounded-full" />
                </span>
              </h2>

              <div className="space-y-3">
                <p className="text-base text-[#2D3A50] leading-relaxed">
                  {companyInfo.description}
                </p>

                {/* Human quote - more prominent */}
                <div className="relative bg-gradient-to-r from-[#4A7DBF]/4 to-transparent rounded-xl p-4 border-l-[3px] border-[#4A7DBF]/40">
                  <i className="ri-double-quotes-l text-2xl text-[#4A7DBF]/25 absolute top-3 left-4" />
                  <p className="text-sm text-[#3A4A62] italic leading-relaxed pl-6">
                    {t("about.quote")}
                  </p>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                {aboutStats(t).map((stat, idx) => (
                  <div
                    key={stat.label}
                    className="group p-3.5 rounded-xl bg-white border border-[#E8EDF4] hover:border-[#4A7DBF]/30 transition-all duration-400 relative overflow-hidden cursor-default"
                    style={{
                      boxShadow: "0 2px 12px rgba(0,0,0,0.02)",
                      transitionDelay: `${idx * 80}ms`,
                    }}
                  >
                    {/* Hover accent glow */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#4A7DBF]/3 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <div className="relative z-10">
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#4A7DBF]/6 mb-1.5">
                        <i className={`${stat.icon} text-xs text-[#4A7DBF]`} />
                      </div>
                      <p className="font-heading font-bold text-lg text-[#1A2B4A] group-hover:text-[#4A7DBF] transition-colors">
                        <AnimatedCounter
                          target={stat.value}
                          decimals={stat.value % 1 !== 0 ? 1 : 0}
                          isVisible={isVisible}
                          suffix={stat.suffix}
                        />
                      </p>
                      <p className="text-[10px] font-label text-[#7A8BA8] uppercase tracking-wider">{stat.label}</p>
                      <p className="text-[10px] text-[#A0AEC0] mt-0.5">{stat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* === BOTTOM ROW: Values + Certifications === */}
          <div
            className={`grid grid-cols-1 lg:grid-cols-3 gap-3 mb-8 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {aboutValues(t).map((v, idx) => {
              const isExpanded = expandedValues.has(idx);
              const toggle = () => {
                setExpandedValues((prev) => {
                  const next = new Set(prev);
                  if (next.has(idx)) next.delete(idx);
                  else next.add(idx);
                  return next;
                });
              };
              return (
                <div
                  key={v.title}
                  onClick={toggle}
                  className="group relative overflow-hidden rounded-xl border border-[#4A7DBF]/10 bg-[#0F1C2E] p-4 transition-all duration-400 hover:border-[#4A7DBF]/25 cursor-pointer"
                  style={{ transitionDelay: `${idx * 80}ms` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#4A7DBF]/10 border border-[#4A7DBF]/15 flex-shrink-0">
                      <i className={`${v.icon} text-sm text-[#4A7DBF]`} />
                    </div>
                    <h5 className="font-heading font-semibold text-sm text-white">{v.title}</h5>
                    <div className="ml-auto text-[#4A7DBF]/40">
                      <i className={`text-xs ${isExpanded ? "ri-subtract-line" : "ri-add-line"}`} />
                    </div>
                  </div>

                  <div
                    className={`overflow-hidden transition-all duration-400 ease-in-out ${
                      isExpanded ? "max-h-32 opacity-100 mt-3" : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="text-xs text-foreground-300/70 leading-relaxed pl-11">
                      {v.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Certifications bar */}
          <div
            className={`flex flex-wrap items-center gap-2.5 transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <span className="text-[11px] font-label font-semibold text-[#7A8BA8] uppercase tracking-wider mr-1">
              {t("about.certificationsLabel")}
            </span>
            <span className="w-px h-4 bg-[#D0D8E8]" />
            {companyInfo.certifications.map((cert) => (
              <button
                key={cert}
                type="button"
                onClick={() => onOpenModal("nosotros")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D8E0EE] bg-white text-[11px] font-label text-[#4A7DBF] hover:border-[#4A7DBF]/50 hover:bg-[#4A7DBF]/[0.03] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-verified-badge-line text-[#4A7DBF] text-xs" />
                {cert}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AnimatedCounter({
  target,
  decimals,
  isVisible,
  suffix,
}: {
  target: number;
  decimals: number;
  isVisible: boolean;
  suffix: string;
}) {
  const value = useAnimatedCounter(target, decimals, isVisible);
  return (
    <span>
      {value}
      {suffix}
    </span>
  );
}